/**
 * IMPORTANT: This is the PRIMARY useSpreadElements hook used by BookSpread.jsx
 * Location: /src/hooks/useSpreadElements.js
 * 
 * DO NOT confuse with: /src/components/BookDesign/hooks/useSpreadElements.js
 * That file is DEPRECATED and NOT used by BookSpread.
 * 
 * This hook is responsible for:
 * - Loading elements from currentBook.spreads[spreadId]
 * - Validating and preserving all element properties (including orientation)
 * - Saving elements back to Firestore
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useBook } from '../contexts/BookContext';

// Helper to remove undefined values from an object recursively
const sanitizeData = (obj) => {
  if (!obj) return obj;
  
  // If the object is an array, process each item
  if (Array.isArray(obj)) {
    return obj.map(item => sanitizeData(item));
  }
  
  // If not an array or object, return as is
  if (typeof obj !== 'object') return obj;
  
  // Process object properties
  const result = {};
  

  
  for (const key in obj) {
    // Skip only undefined values (but keep 0, false, empty string, etc.)
    if (obj[key] === undefined) continue;
    
    // Special handling for zIndex to always ensure it's a number
    if (key === 'zIndex') {
      if (typeof obj[key] === 'number') {
        result[key] = obj[key];
      } else if (typeof obj[key] === 'string' && !isNaN(parseInt(obj[key]))) {
        result[key] = parseInt(obj[key]);
      } else {
        // Default to 1 if invalid
        result[key] = 1;
        console.warn(`[ZINDEX DEBUG] Sanitized invalid zIndex value: ${obj[key]}`);
      }
    } 
    // FIXED: Special handling for ALL border properties to ensure they are preserved
    else if (key === 'hasBorder' || key.startsWith('border')) {
      // Preserve ALL border properties, including numeric zeros, strings, and boolean values
      console.log(`[BorderDebug] Preserving border property ${key}:`, obj[key], typeof obj[key]);
      result[key] = obj[key];
    }
    else {
      // Normal recursive sanitization for other properties
      result[key] = sanitizeData(obj[key]);
    }
  }
  

  
  return result;
};

// Helper to ensure all required fields have non-undefined values
const validateElement = (element, spreadId, index) => {
  console.log('[ZINDEX DEBUG] validateElement called for:', {
    id: element.id,
    type: element.type,
    zIndex: element.zIndex,
    zIndexType: typeof element.zIndex
  });

  // Ensure we have a valid z-index as a number
  let safeZIndex = 1;
  
  if (typeof element.zIndex === 'number') {
    safeZIndex = element.zIndex;
    console.log(`[ZINDEX DEBUG] Element ${element.id} has numeric zIndex: ${safeZIndex}`);
  } else if (typeof element.zIndex === 'string' && !isNaN(parseInt(element.zIndex))) {
    // Only parse if it's a numeric string
    safeZIndex = parseInt(element.zIndex);
    console.log(`[ZINDEX DEBUG] Element ${element.id} has string zIndex: ${element.zIndex}, parsed to ${safeZIndex}`);
  } else {
    console.log(`[ZINDEX DEBUG] Element ${element.id} has invalid zIndex: ${element.zIndex}, defaulting to ${safeZIndex}`);
  }
  
  // For debugging if an ID was mistakenly assigned as zIndex
  if (typeof element.zIndex === 'string' && element.zIndex === element.id) {
    console.warn('[ZINDEX DEBUG] Found element with ID assigned as zIndex, correcting:', {
      id: element.id,
      invalidZIndex: element.zIndex
    });
  }

  // Direct debugging of border properties for image elements
  if (element.type === 'image') {
    console.log(`[VALIDATION] Processing image element ${element.id} border properties:`, {
      hasBorder: element.hasBorder,
      hasBorderType: typeof element.hasBorder,
      borderColor: element.borderColor, 
      borderColorType: typeof element.borderColor,
      borderWidth: element.borderWidth,
      borderWidthType: typeof element.borderWidth,
      borderStyle: element.borderStyle,
      borderStyleType: typeof element.borderStyle,
      borderRadius: element.borderRadius,
      borderRadiusType: typeof element.borderRadius,
      borderOpacity: element.borderOpacity,
      borderOpacityType: typeof element.borderOpacity
    });
    
    // USER REQUESTED LOGGING: Easy to filter logging for validation step
    console.log('testing border props - DURING VALIDATION:', {
      id: element.id, 
      borderWidth: element.borderWidth,
      borderWidthType: typeof element.borderWidth,
      borderStyle: element.borderStyle,
      borderStyleType: typeof element.borderStyle,
      borderRadius: element.borderRadius
    });
  }
  
  // REMOVED: We're no longer pre-processing border properties at all
  // Instead we're preserving the exact values from Firestore
  
  // Instead, just log the raw element for debugging
  if (element.type === 'image') {
    console.log(`[VALIDATION] this is it RAW element ${element.id} properties:`, {
      hasBorder: element.hasBorder,
      borderColor: element.borderColor,
      borderWidth: element.borderWidth,
      borderStyle: element.borderStyle,
      borderRadius: element.borderRadius,
      borderOpacity: element.borderOpacity,
      // Add visualStyle to logging
      visualStyle: element.visualStyle
    });
    
    // Special logging just for visualStyle
    console.log(`🔑 this is it [VISUALSTYLE] Element ${element.id} has visualStyle:`, element.visualStyle);
  }

  // COMPLETE REWRITE: Create an element that preserves ALL Firestore values
  const validatedElement = {
    // Keep original ID, fall back to docId (jobs reference elements this way),
    // then to the array index — the same canonical id bulk image jobs use
    // (element.id || element.docId || elementKey). A random id would never match
    // job.elementId for elements queued via imageUpdateJobs.
    id: element.id || element.docId || (index != null ? String(index) : crypto.randomUUID()),
    // Preserve docId so dirty-merge lookups can match on it
    ...(element.docId ? { docId: element.docId } : {}),
    // Keep original type or default to text
    type: element.type || 'text',
    // Always track spread ID
    spreadId: spreadId,
    // For text elements, preserve content
    content: element.content || '',
    // For position and size, preserve original values exactly as they are
    position: element.position ? {
      x: element.position.x !== undefined ? element.position.x : 0,
      y: element.position.y !== undefined ? element.position.y : 0,
    } : { x: 0, y: 0 },
    size: element.size ? {
      width: element.size.width !== undefined ? element.size.width : 100,
      height: element.size.height !== undefined ? element.size.height : 100,
    } : { width: 100, height: 100 },
    // Preserve style object without defaults
    style: element.style || {},
    // Use validated zIndex
    zIndex: safeZIndex,
    // For image elements, preserve ALL image-specific properties
    ...(element.type === 'image'
      ? {
          // General image properties
          // Handle both new array structure and old string structure for backward compatibility
          imagePrompts: Array.isArray(element.imagePrompts) ? element.imagePrompts : 
                        (element.imagePrompt ? [element.imagePrompt] : []),
          images: Array.isArray(element.images) ? element.images : [],
          src: element.src || '',
          imageStatus: element.imageStatus || 'pending',
          imageError: element.imageError || null,
          imageGenerationStatus: element.imageGenerationStatus,
          imageUpdateJobId: element.imageUpdateJobId,
          lastGenerationFailed: element.lastGenerationFailed || false,
          lastGenerationError: element.lastGenerationError || null,
          // Include visualStyle property
          visualStyle: element.visualStyle,
          // Include orientation property
          orientation: element.orientation,
          
          // CRITICAL FIX: Preserve ALL border properties EXACTLY as they are in Firestore
          // WITHOUT any type conversion or default values that could alter them
          hasBorder: element.hasBorder,
          borderColor: element.borderColor,
          borderWidth: element.borderWidth,
          borderStyle: element.borderStyle,
          borderRadius: element.borderRadius,
          borderOpacity: element.borderOpacity
        }
      : {}),
    ...(element.type === 'text' && element.audio
      ? {
          audio: element.audio,
        }
      : {}),
    ...(element.type === 'shape'
      ? {
          shape: element.shape || 'square',
        }
      : {}),
  };
  
  console.log('[ZINDEX DEBUG] Element after validation:', {
    id: validatedElement.id,
    type: validatedElement.type,
    zIndex: validatedElement.zIndex,
    zIndexType: typeof validatedElement.zIndex
  });
  
  // Special logging for image elements to verify visualStyle is preserved
  if (validatedElement.type === 'image') {
    // Extra protection for visualStyle - ensure it's explicitly preserved
    if (element.visualStyle !== undefined && validatedElement.visualStyle === undefined) {
      console.log(`⚠️ [VISUALSTYLE] Style lost during validation for ${validatedElement.id.slice(0,8)}! Restoring:`, element.visualStyle);
      validatedElement.visualStyle = element.visualStyle;
    } else if (element.visualStyle !== undefined && validatedElement.visualStyle !== element.visualStyle) {
      console.log(`⚠️ [VISUALSTYLE] Style changed during validation for ${validatedElement.id.slice(0,8)}! Original: ${element.visualStyle}, New: ${validatedElement.visualStyle}`);
    }
    
    console.log(`🔎 [VISUALSTYLE] After validation, element ${validatedElement.id.slice(0,8)} has visualStyle:`, validatedElement.visualStyle);
  }
  
  return validatedElement;
};

// Merge server-side image state into a locally-edited element without touching
// user-editable fields (position, size unless it actually changed, etc).
// Returns the local element unchanged when nothing relevant differs.
export const mergeIncomingImageElement = (localEl, incoming) => {
  const sizeChanged = incoming.size && (
    incoming.size.width !== localEl.size?.width ||
    incoming.size.height !== localEl.size?.height
  );
  const jobFieldsChanged =
    incoming.imageGenerationStatus !== localEl.imageGenerationStatus ||
    (incoming.imageUpdateJobId ?? null) !== (localEl.imageUpdateJobId ?? null) ||
    !!incoming.lastGenerationFailed !== !!localEl.lastGenerationFailed ||
    (incoming.lastGenerationError ?? null) !== (localEl.lastGenerationError ?? null);

  if (
    incoming.src !== localEl.src ||
    incoming.imageStatus !== localEl.imageStatus ||
    incoming.error !== localEl.error ||
    sizeChanged ||
    jobFieldsChanged
  ) {
    return {
      ...localEl,
      // Converge on the canonical id Firestore knows (bulk jobs write `id:`)
      id: incoming.id || incoming.docId || localEl.id,
      src: incoming.src,
      imageStatus: incoming.imageStatus,
      error: incoming.error,
      imageGenerationStatus: incoming.imageGenerationStatus,
      imageUpdateJobId: incoming.imageUpdateJobId,
      lastGenerationFailed: incoming.lastGenerationFailed,
      lastGenerationError: incoming.lastGenerationError,
      ...(sizeChanged && { size: incoming.size }), // Include size if it changed
    };
  }
  return localEl;
};

export function useSpreadElements(bookId, spreadId) {
  const [elements, setElements] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const { currentBook, updateSpread } = useBook();
  // Ref mirrors so loadElements can read current values without being in its dependency array
  const isDirtyRef = useRef(false);
  useEffect(() => { isDirtyRef.current = isDirty; }, [isDirty]);
  const elementsRef = useRef([]);
  useEffect(() => { elementsRef.current = elements; }, [elements]);

  // Debug whenever elements state changes
  useEffect(() => {
    console.log('DEBUG useSpreadElements: Elements state changed:', {
      spreadId,
      elementCount: elements.length,
      elements: elements.map((el) => ({
        id: el.id,
        type: el.type,
        content: el.type === 'text' ? el.content : undefined,
        position: el.position,
        ...(el.type === 'image'
          ? {
              imagePrompts: el.imagePrompts,
              promptCount: el.imagePrompts?.length || 0,
              images: el.images,
              imageCount: el.images?.length || 0,
              src: el.src,
            }
          : {}),
      })),
    });
  }, [elements, spreadId]);

  // Debug whenever currentBook changes
  useEffect(() => {
    if (currentBook) {
      console.log('DEBUG useSpreadElements: Current book updated:', {
        bookId: currentBook.id,
        spreadId,
        hasSpreadData: !!currentBook.spreads?.[spreadId],
        elementCount: currentBook.spreads?.[spreadId]?.elements?.length || 0,
      });
    }
  }, [currentBook, spreadId]);

  // NOTE: Removed direct onSnapshot listeners for both subdoc and parent book.
  // These were causing race conditions where stale/cached snapshot data would
  // overwrite elements that were correctly loaded via loadElements().
  // Real-time updates are already handled by BookContext's own onSnapshot listener
  // which updates currentBook, triggering loadElements() via the dependency chain.

  const loadElements = useCallback(() => {
    console.log('DEBUG useSpreadElements: Loading elements:', {
      bookId,
      spreadId,
      hasBook: !!currentBook,
      hasSpreadData: !!currentBook?.spreads?.[spreadId],
    });

    if (!bookId || !spreadId || !currentBook) {
      console.log('DEBUG useSpreadElements: Missing required data:', {
        bookId,
        spreadId,
        hasBook: !!currentBook,
      });
      return;
    }

    // When dirty, selectively merge only image src/status from Firestore
    // to show completed generations without overwriting local position/size edits
    if (isDirtyRef.current) {
      const incomingElements = currentBook.spreads?.[spreadId]?.elements || [];
      if (incomingElements.length > 0) {
        const incomingById = {};
        incomingElements.forEach(el => {
          if (el.id) incomingById[el.id] = el;
          if (el.docId && !incomingById[el.docId]) incomingById[el.docId] = el;
        });
        const sameLength = incomingElements.length === elementsRef.current.length;
        let hasImageUpdate = false;
        const merged = elementsRef.current.map((localEl, idx) => {
          if (localEl.type !== 'image') return localEl;
          let incoming = incomingById[localEl.id] || (localEl.docId ? incomingById[localEl.docId] : undefined);
          // Elements whose local id was randomly assigned (never had a stable
          // id/docId in Firestore) can't match by key — when lengths and types
          // align, the array index is the only shared identity.
          if (!incoming && sameLength && !localEl.docId) {
            const candidate = incomingElements[idx];
            if (candidate && candidate.type === 'image') incoming = candidate;
          }
          if (!incoming) return localEl;
          const next = mergeIncomingImageElement(localEl, incoming);
          if (next !== localEl) hasImageUpdate = true;
          return next;
        });
        if (hasImageUpdate) {
          console.log('DEBUG useSpreadElements: Selective image merge for', spreadId);
          setElements(merged);
          // Keep isDirty true — user still has unsaved position/size changes
        }
      }
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Get elements directly from the book's spreads object
      const spreadElements = currentBook.spreads?.[spreadId]?.elements || [];
      
      // Add debugging just for visualStyle
      console.log("🎨 LOAD DEBUG: Raw element visualStyles:", 
        spreadElements
          .filter(el => el.type === 'image')
          .map(el => ({id: el.id.slice(0,8), visualStyle: el.visualStyle}))
      );
      
      // CRITICAL FIX: Log raw elements from Firestore BEFORE any processing
      console.log('[FIRESTORE RAW DATA] Original elements data:', 
        JSON.stringify(spreadElements.filter(el => el.type === 'image').map(el => ({
          id: el.id,
          hasBorder: el.hasBorder,
          borderColor: el.borderColor,
          borderWidth: el.borderWidth,
          borderStyle: el.borderStyle,
          borderRadius: el.borderRadius,
          borderOpacity: el.borderOpacity
        })), null, 2)
      );
      
      // USER REQUESTED LOGGING: Display border properties in filterable format
      const imageElementsWithBorders = spreadElements.filter(el => el.type === 'image');
      console.log('testing border props - FROM FIRESTORE [ORIGINAL VALUES]:', 
        imageElementsWithBorders.map(el => ({
          id: el.id,
          borderWidth: el.borderWidth,
          borderWidthType: typeof el.borderWidth,
          borderStyle: el.borderStyle,
          borderStyleType: typeof el.borderStyle,
          borderRadius: el.borderRadius,
          borderRadiusType: typeof el.borderRadius,
          borderOpacity: el.borderOpacity,
          borderOpacityType: typeof el.borderOpacity
        }))
      );

      // Ensure all elements are properly validated
      const validatedElements = spreadElements.map((el, index) => {
        const validated = validateElement(el, spreadId, index);
        
        // Check if border properties changed during validation
        if (el.type === 'image') {
          // Add debugging to check if visualStyle survived validation
          console.log(`🎨 VALIDATION: Element ${el.id.slice(0,8)} visualStyle:`, {
            before: el.visualStyle, 
            after: validated.visualStyle
          });
          console.log(`[DATA SYNC] Element ${el.id} border properties BEFORE vs AFTER validation:`, {
            before: {
              hasBorder: el.hasBorder,
              borderColor: el.borderColor,
              borderWidth: el.borderWidth,
              borderStyle: el.borderStyle,
              borderRadius: el.borderRadius,
              borderOpacity: el.borderOpacity
            },
            after: {
              hasBorder: validated.hasBorder,
              borderColor: validated.borderColor,
              borderWidth: validated.borderWidth,
              borderStyle: validated.borderStyle,
              borderRadius: validated.borderRadius,
              borderOpacity: validated.borderOpacity
            }
          });
        }
        
        return validated;
      });

      // Enhanced logging to verify border properties are correctly loaded
      console.log('DEBUG useSpreadElements: Found elements:', {
        count: validatedElements.length,
        elements: validatedElements.map((el) => ({
          id: el.id,
          type: el.type,
          content: el.type === 'text' ? el.content : undefined,
          position: el.position,
          ...(el.type === 'image'
            ? {
                imagePrompts: el.imagePrompts,
                promptCount: el.imagePrompts?.length || 0,
                images: el.images,
                imageCount: el.images?.length || 0,
                src: el.src,
                // Explicitly log all border properties to verify they're loaded correctly
                hasBorder: el.hasBorder,
                borderColor: el.borderColor,
                borderWidth: el.borderWidth,
                borderStyle: el.borderStyle,
                borderRadius: el.borderRadius,
                borderOpacity: el.borderOpacity
              }
            : {}),
        })),
      });
      
      // Special debugging for image elements and their border properties
      const imageElements = validatedElements.filter(el => el.type === 'image');
      if (imageElements.length > 0) {
        console.log('[BorderDebug] Loaded image elements with border properties:', 
          imageElements.map(el => ({
            id: el.id,
            hasBorder: el.hasBorder,
            borderColor: el.borderColor,
            borderWidth: el.borderWidth,
            borderStyle: el.borderStyle,
            borderRadius: el.borderRadius,
            borderOpacity: el.borderOpacity
          }))
        );
      }

      setElements(validatedElements);
      setIsDirty(false);
    } catch (err) {
      console.error('DEBUG useSpreadElements: Error loading elements:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [bookId, spreadId, currentBook]);

  const updateElements = useCallback(
    (newElements) => {
      console.log('[ZINDEX DEBUG] === USESPREADELEMENTS.UPDATEELEMENTS START ===');
      console.log('[ZINDEX DEBUG] updateElements called with new elements:', {
        spreadId,
        elementCount: newElements.length,
        elements: newElements.map((el) => ({
          id: el.id,
          type: el.type,
          zIndex: el.zIndex,
          zIndexType: typeof el.zIndex,
        })),
      });

      // Find differences between current elements and new elements
      if (elements.length > 0) {
        console.log('[ZINDEX DEBUG] Comparing with current elements:');
        elements.forEach((currentEl) => {
          const newEl = newElements.find((el) => el.id === currentEl.id);
          if (newEl) {
            if (newEl.zIndex !== currentEl.zIndex) {
              console.log('[ZINDEX DEBUG] Element z-index changed:', {
                id: currentEl.id,
                type: currentEl.type,
                oldZIndex: currentEl.zIndex,
                oldZIndexType: typeof currentEl.zIndex,
                newZIndex: newEl.zIndex,
                newZIndexType: typeof newEl.zIndex,
              });
            }
          }
        });
      }

      // Make sure all elements are valid before updating state
      const validElements = newElements.map((el, index) =>
        validateElement(el, spreadId, index)
      );

      console.log('[ZINDEX DEBUG] Elements after validation:', {
        count: validElements.length,
        elements: validElements.map((el) => ({
          id: el.id,
          type: el.type,
          zIndex: el.zIndex,
          zIndexType: typeof el.zIndex,
        })),
      });

      // Update the state with the validated elements
      console.log('[ZINDEX DEBUG] Setting elements state...');
      setElements(validElements);

      // Mark as dirty to indicate changes need to be saved
      setIsDirty(true);

      console.log('[ZINDEX DEBUG] === USESPREADELEMENTS.UPDATEELEMENTS END ===');
    },
    [spreadId, elements]
  );

  const saveElements = useCallback(async () => {

    
    if (!isDirty) {
      console.log('DEBUG useSpreadElements: No changes to save');
      return;
    }

    if (!bookId || !spreadId) {
      console.error('DEBUG useSpreadElements: Missing bookId or spreadId', {
        bookId,
        spreadId,
      });
      return;
    }
    

    console.log('this is it',elements)
    // Check for visualStyle in image elements
    console.log('this is it Image elements with visualStyle:', 
      elements.filter(el => el.type === 'image').map(el => ({
        id: el.id, 
        visualStyle: el.visualStyle
      }))
    );
    
    // Return statement removed for testing saving
    console.log('[ZINDEX DEBUG] === SAVE ELEMENTS START ===');
    console.log('[ZINDEX DEBUG] Initial state before save:', {
      bookId,
      spreadId,
      elementCount: elements.length,
      elements: elements.map(el => ({
        id: el.id, 
        type: el.type,
        zIndex: el.zIndex,
        zIndexType: typeof el.zIndex
      }))
    });

    try {
      console.log('[SAVE DEBUG] saveElements called with:', { bookId, spreadId, isDirty, isSaving, elementCount: elements?.length });
      
      setIsSaving(true);

      // Safeguard against null elements array
      if (!elements) {
        console.error('[SAVE DEBUG] Elements array is null or undefined during save!');
        return;
      }

      // Validate all elements to ensure they have proper values
      const validatedElements = elements.map((el, index) => validateElement(el, spreadId, index));
      
      // Special logging for visualStyle before save
      console.log('🎨 FINAL SAVE: Image elements with visualStyle:', 
        validatedElements
          .filter(el => el.type === 'image')
          .map(el => ({
            id: el.id.slice(0,8),
            visualStyle: el.visualStyle,
            hasVisualStyle: el.visualStyle !== undefined,
            imageGenerationStatus: el.imageGenerationStatus,
            imageUpdateJobId: el.imageUpdateJobId
          }))
      );

      console.log('[ZINDEX DEBUG] Elements after validation for save:', {
        count: validatedElements.length,
        elements: validatedElements.map(el => ({
          id: el.id,
          type: el.type,
          zIndex: el.zIndex,
          zIndexType: typeof el.zIndex
        }))
      });

      // Build the updated spread data for ONLY the current spread
      const updatedSpreadData = {
        ...(currentBook?.spreads?.[spreadId] || {}),
        elements: validatedElements,
      };

      // Sanitize only the current spread data
      const sanitizedSpreadData = sanitizeData(updatedSpreadData);

      console.log('[SAVE] Saving ONLY spread:', {
        spreadId,
        elementCount: sanitizedSpreadData?.elements?.length,
        elements: sanitizedSpreadData?.elements?.map(el => ({
          id: el.id,
          type: el.type,
          zIndex: el.zIndex,
          zIndexType: typeof el.zIndex
        }))
      });

      // Use Firestore dot notation to update ONLY the affected spread field
      const bookRef = doc(db, 'books', bookId);
      await updateDoc(bookRef, {
        [`spreads.${spreadId}`]: sanitizedSpreadData,
        updatedAt: serverTimestamp(),
      });

      // Update local BookContext state for just this spread
      updateSpread(spreadId, sanitizedSpreadData);

      // Mark as saved (no longer dirty)
      setIsDirty(false);
      console.log('[ZINDEX DEBUG] Elements saved successfully');
    } catch (error) {
      console.error('[ZINDEX DEBUG] Error saving elements:', error);
    } finally {
      setIsSaving(false);
      console.log('[ZINDEX DEBUG] === SAVE ELEMENTS END ===');
    }
  }, [
    bookId,
    spreadId,
    isDirty,
    elements,
    currentBook,
    updateSpread,
  ]);

  return {
    elements,
    isLoading,
    error,
    isDirty,
    isSaving,
    loadElements,
    updateElements,
    saveElements,
  };
}
